import { API_ERROR_STATUS } from 'constants/api';
import { saveBlob } from 'helper/downloadFile';
import { getContentDispositionFileName } from 'helper/getContentDispositionFileName';

const DEFAULT_RETRY_DELAY_MS = 5000;
const MAX_RETRY_DELAY_MS = 30000;
const DEFAULT_MAX_ATTEMPTS = 3;

export const PDF_EXPORT_ENDPOINT_NAMES = [
  'exportPersonalJournalReportPdf',
  'exportTeamReportPdf',
  'getNomineeReportPdf',
  'exportProjectJournalEntryPdf',
  'exportProjectJournalEntriesZip',
  'exportProjectQrPdf',
];

const pdfResponseHandler = async (response) => {
  if (response.ok) {
    return response.blob();
  }

  const text = await response.text();

  return text ? JSON.parse(text) : null;
};

const isThrottled = (error) =>
  error?.status === API_ERROR_STATUS.TOO_MANY_REQUESTS || error?.originalStatus === API_ERROR_STATUS.TOO_MANY_REQUESTS;

const wait = (ms) =>
  new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });

const getRetryDelayMs = (response) => {
  const retryAfter = response?.headers.get('Retry-After');

  if (!retryAfter) {
    return DEFAULT_RETRY_DELAY_MS;
  }

  const seconds = Number(retryAfter);

  if (Number.isFinite(seconds)) {
    return seconds * 1000;
  }

  const retryAt = Date.parse(retryAfter);

  return Number.isNaN(retryAt) ? DEFAULT_RETRY_DELAY_MS : Math.max(retryAt - Date.now(), 0);
};

export const fetchPdf = async (baseQueryFn, request, { maxAttempts = DEFAULT_MAX_ATTEMPTS, attempt = 1 } = {}) => {
  const result = await baseQueryFn({ ...request, responseHandler: pdfResponseHandler });

  if (isThrottled(result.error) && attempt < maxAttempts) {
    await wait(Math.min(getRetryDelayMs(result.meta?.response), MAX_RETRY_DELAY_MS));

    return fetchPdf(baseQueryFn, request, { maxAttempts, attempt: attempt + 1 });
  }

  if (result.error) {
    return { error: result.error };
  }

  return {
    blob: result.data,
    fileName: getContentDispositionFileName(result.meta?.response?.headers.get('Content-Disposition')),
  };
};

export const fetchPdfsWithConcurrency = async (baseQueryFn, requests, { limit, maxAttempts }) => {
  const results = [];
  let nextIndex = 0;
  let error = null;

  const runWorker = async () => {
    if (error || nextIndex >= requests.length) {
      return;
    }

    const index = nextIndex;

    nextIndex += 1;
    results[index] = await fetchPdf(baseQueryFn, requests[index], { maxAttempts });
    error = error ?? results[index].error ?? null;

    await runWorker();
  };

  await Promise.all(Array.from({ length: Math.min(limit, requests.length) }, runWorker));

  return error ? { error } : { data: results };
};

export const createPdfExportQueryFn = (getRequest, fallbackFileName) => async (arg, api, extraOptions, baseQueryFn) => {
  const { blob, fileName, error } = await fetchPdf(baseQueryFn, getRequest(arg));

  if (error) {
    return { error };
  }

  saveBlob(blob, fileName || fallbackFileName);

  return { data: null };
};
